import api from '@/lib/api';
import { generateFields } from '@/api/generators-api';
import type { FieldDefinition } from '@/types/fields-api.model';
import type { FieldsToType } from '@/lib/type-utils';

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

/**
 * Generic API function that fetches data with dynamic typing based on field selectors.
 *
 * @template TModel - The complete model type (e.g., SportData)
 * @template TDefaultFields - The default field selector type
 *
 * @param endpoint - The API endpoint to call
 * @param defaultFields - The default field selector to use
 * @param method - HTTP method to use
 *
 * @returns A function that accepts field selectors, data, and returns typed data
 */
export function createApiWithFields<TModel, TDefaultFields extends object>(
  endpoint: string,
  defaultFields: TDefaultFields,
  method: HttpMethod = HttpMethod.GET,
) {
  return async <T extends object = TDefaultFields>(
    fields: T = defaultFields as unknown as T,
    data?: unknown,
    additionalConfig?: unknown,
  ): Promise<FieldsToType<T, TModel>[]> => {
    const config = {
      params: {
        fields: generateFields(fields as FieldDefinition),
      },
      ...(additionalConfig as Record<string, unknown>),
    };

    let response;

    switch (method) {
      case HttpMethod.GET:
        response = await api.get(endpoint, config);
        break;
      case HttpMethod.POST:
        response = await api.post(endpoint, data, config);
        break;
      case HttpMethod.PUT:
        response = await api.put(endpoint, data, config);
        break;
      case HttpMethod.DELETE:
        response = await api.delete(endpoint, config);
        break;
      case HttpMethod.PATCH:
        response = await api.patch(endpoint, data, config);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return response.data;
  };
}

/**
 * Generic API function that fetches a single item with dynamic typing based on field selectors.
 *
 * @template TModel - The complete model type (e.g., User)
 * @template TDefaultFields - The default field selector type
 *
 * @param endpoint - The API endpoint to call
 * @param defaultFields - The default field selector to use
 * @param method - HTTP method to use
 *
 * @returns A function that accepts field selectors, data, and returns typed data
 */
export function createSingleApiWithFields<
  TModel,
  TDefaultFields extends object,
>(
  endpoint: string,
  defaultFields: TDefaultFields,
  method: HttpMethod = HttpMethod.GET,
) {
  return async <T extends object = TDefaultFields>(
    fields: T = defaultFields as unknown as T,
    data?: unknown,
    additionalConfig?: unknown,
  ): Promise<FieldsToType<T, TModel>> => {
    const config = {
      params: {
        fields: generateFields(fields as FieldDefinition),
      },
      ...(additionalConfig as Record<string, unknown>),
    };

    let response;

    switch (method) {
      case HttpMethod.GET:
        response = await api.get(endpoint, config);
        break;
      case HttpMethod.POST:
        response = await api.post(endpoint, data, config);
        break;
      case HttpMethod.PUT:
        response = await api.put(endpoint, data, config);
        break;
      case HttpMethod.DELETE:
        response = await api.delete(endpoint, config);
        break;
      case HttpMethod.PATCH:
        response = await api.patch(endpoint, data, config);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return response.data;
  };
}
